// Cliente HTTP único del frontend. Mismo origen (Vite hace de proxy en desarrollo/UAT), cookie de
// sesión httpOnly, y TODA respuesta de error pasa por `ErrorApi` con el sobre del backend intacto:
// la traducción a un mensaje operacional la hace `errores/catalogo.ts`, nunca cada pantalla.
import type { CuerpoError } from './contratos';

export class ErrorApi extends Error {
  constructor(
    readonly status: number,
    readonly cuerpo: CuerpoError,
    readonly correlacionId: string | null,
  ) {
    super(cuerpo.mensaje);
    this.name = 'ErrorApi';
  }
  /** Código de dominio (`detalle.codigo`) o, si no hay, la categoría (`error`). */
  get codigo(): string {
    const c = this.cuerpo.detalle?.codigo;
    return typeof c === 'string' && c ? c : this.cuerpo.error;
  }
}

type Metodo = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export interface OpcionesPedido {
  cuerpo?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Clave de idempotencia del INTENTO (la misma si se reintenta el mismo intento). */
  idempotencia?: string;
  signal?: AbortSignal;
  /** No avisar sesión expirada (login y comprobación inicial). */
  silenciar401?: boolean;
}

let alSesionExpirada: (() => void) | null = null;
/** El contexto de sesión se registra aquí para enterarse de un 401 en cualquier pedido. */
export function registrarSesionExpirada(fn: (() => void) | null): void {
  alSesionExpirada = fn;
}

/**
 * Prefijo de la API en el borde (UAT: Caddy recibe `/api/*`, quita el prefijo y reenvía a la API).
 * Se fija al CONSTRUIR (`VITE_API_BASE=/api`); en desarrollo y E2E queda vacío y Vite proxya cada ruta.
 */
const BASE_API = import.meta.env['VITE_API_BASE'] ?? '';

/** `rutaConBase('/api', '/ventas/notas')` → `/api/ventas/notas`; sin base, la ruta tal cual. */
export function rutaConBase(base: string, ruta: string): string {
  const b = base.replace(/\/+$/, '');
  return b ? `${b}${ruta}` : ruta;
}

function url(rutaApi: string, query?: OpcionesPedido['query']): string {
  const ruta = rutaConBase(BASE_API, rutaApi);
  if (!query) return ruta;
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(query))
    if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  const qs = p.toString();
  return qs ? `${ruta}?${qs}` : ruta;
}

export async function pedir<T>(metodo: Metodo, ruta: string, o: OpcionesPedido = {}): Promise<T> {
  const cabeceras: Record<string, string> = { accept: 'application/json' };
  if (o.cuerpo !== undefined) cabeceras['content-type'] = 'application/json';
  if (o.idempotencia) cabeceras['idempotency-key'] = o.idempotencia;
  let r: Response;
  try {
    r = await fetch(url(ruta, o.query), {
      method: metodo,
      headers: cabeceras,
      credentials: 'same-origin',
      ...(o.cuerpo !== undefined ? { body: JSON.stringify(o.cuerpo) } : {}),
      ...(o.signal ? { signal: o.signal } : {}),
    });
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    throw new ErrorApi(
      0,
      { error: 'sin_conexion', mensaje: 'No hay conexión con el servidor', detalle: null },
      null,
    );
  }
  const correlacion = r.headers.get('x-correlacion-id');
  const texto = await r.text();
  const json: unknown = texto ? JSON.parse(texto) : null;
  if (!r.ok) {
    const cuerpo: CuerpoError =
      json && typeof json === 'object' && 'error' in json
        ? (json as CuerpoError)
        : { error: `http_${r.status}`, mensaje: `Respuesta ${r.status}`, detalle: null };
    if (r.status === 401 && !o.silenciar401) alSesionExpirada?.();
    throw new ErrorApi(r.status, cuerpo, cuerpo.correlacion_id ?? correlacion);
  }
  return json as T;
}

export const api = {
  get: <T>(ruta: string, o?: OpcionesPedido) => pedir<T>('GET', ruta, o),
  post: <T>(ruta: string, cuerpo?: unknown, o?: OpcionesPedido) =>
    pedir<T>('POST', ruta, { ...o, cuerpo: cuerpo ?? {} }),
  patch: <T>(ruta: string, cuerpo: unknown, o?: OpcionesPedido) =>
    pedir<T>('PATCH', ruta, { ...o, cuerpo }),
  put: <T>(ruta: string, cuerpo: unknown, o?: OpcionesPedido) =>
    pedir<T>('PUT', ruta, { ...o, cuerpo }),
  delete: <T>(ruta: string, o?: OpcionesPedido) => pedir<T>('DELETE', ruta, o),
};

/**
 * Un archivo (PDF, ZIP): la respuesta como Blob. Los errores pasan por `ErrorApi` igual que el resto
 * (el backend responde JSON cuando algo falla).
 */
export async function pedirArchivo(
  metodo: 'GET' | 'POST',
  ruta: string,
  o: { cuerpo?: unknown; query?: OpcionesPedido['query'] } = {},
): Promise<Blob> {
  let r: Response;
  try {
    r = await fetch(url(ruta, o.query), {
      method: metodo,
      credentials: 'same-origin',
      headers: o.cuerpo !== undefined ? { 'content-type': 'application/json' } : {},
      ...(o.cuerpo !== undefined ? { body: JSON.stringify(o.cuerpo) } : {}),
    });
  } catch {
    throw new ErrorApi(
      0,
      { error: 'sin_conexion', mensaje: 'No hay conexión con el servidor', detalle: null },
      null,
    );
  }
  if (!r.ok) {
    const texto = await r.text();
    let cuerpo: CuerpoError = {
      error: `http_${r.status}`,
      mensaje: `Respuesta ${r.status}`,
      detalle: null,
    };
    try {
      const j = JSON.parse(texto) as unknown;
      if (j && typeof j === 'object' && 'error' in j) cuerpo = j as CuerpoError;
    } catch {
      /* no era JSON */
    }
    if (r.status === 401) alSesionExpirada?.();
    throw new ErrorApi(
      r.status,
      cuerpo,
      cuerpo.correlacion_id ?? r.headers.get('x-correlacion-id'),
    );
  }
  return r.blob();
}

/** Guarda un Blob como archivo (descarga del navegador). */
export function guardarArchivo(b: Blob, nombre: string): void {
  const enlace = document.createElement('a');
  enlace.href = URL.createObjectURL(b);
  enlace.download = nombre;
  document.body.appendChild(enlace);
  enlace.click();
  enlace.remove();
  setTimeout(() => URL.revokeObjectURL(enlace.href), 10_000);
}

/** Una clave por intento de acción del usuario (8–200 caracteres, como exige la API). */
export function nuevaClaveIdempotencia(prefijo = 'web'): string {
  return `${prefijo}-${crypto.randomUUID()}`;
}
